import { useTranslation } from 'react-i18next';
import StandardChart from './StandardChart';
import { type ChartData } from './chartConfigUtils';

export interface ValuePoint {
    readonly date: string;
    readonly value: number;
    /** Extrapolated point (running month scaled to a full month). */
    readonly projected?: boolean;
}

interface ValueLineChartProps {
    readonly data: readonly ValuePoint[];
    readonly loading?: boolean;
    readonly referenceValue?: number;
    readonly seriesNameKey: string;
    readonly seriesNameFallback: string;
    readonly color: string;
    readonly currency?: 'EUR' | 'LEI';
}

function ValueLineChart({
    data,
    loading = false,
    referenceValue,
    seriesNameKey,
    seriesNameFallback,
    color,
    currency,
}: Readonly<ValueLineChartProps>): JSX.Element {
    const { t } = useTranslation();
    const chartData: ChartData[] = data.map(d => ({
        date: d.date,
        value: d.value ?? 0,
        projected: d.projected,
    }));
    const isEur = currency === 'EUR';
    const suffix = isEur
        ? (t('statistics.currencyEur', ' EUR') ?? ' EUR')
        : (t('statistics.currencyLei', ' Lei') ?? ' Lei');
    const yAxisKey = isEur ? 'statistics.charts.valueEur' : 'statistics.charts.valueLei';
    const yAxisFallback = isEur ? 'Value (EUR)' : 'Value (Lei)';

    return (
        <StandardChart
            data={chartData}
            loading={loading}
            referenceValue={referenceValue}
            config={{
                chartType: 'line',
                yAxisTitleKey: yAxisKey,
                yAxisTitleFallback: yAxisFallback,
                yAxisColor: color,
                seriesNameKey,
                seriesNameFallback,
                seriesColor: color,
                valueSuffix: suffix,
            }}
        />
    );
}

export default ValueLineChart;
